import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TbPig, TbChevronLeft } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import CurrencyInput from '../../components/forms/CurrencyInput';
import potService, {
  CreatePotDto,
  ContributionFrequency,
} from '../../../services/community/potService';
import { Currency } from '../../../types/bankAccount';
import useSelectedDashboardId from '../../../hooks/community/useSelectedDashboardId';

const PRESET_COLORS = [
  '#0A1B89', // Primary blue
  '#F19E7A', // Secondary peach
  '#8AC285', // Success green
  '#D8273E', // Danger red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#10B981', // Emerald
  '#F59E0B', // Amber
];

interface PotFormData {
  name: string;
  currency: Currency;
  description?: string;
  color: string;
  targetAmount?: number; // in cents
  targetDeadline?: string;
  depositsEnabled: boolean;
  withdrawalsEnabled: boolean;
  automaticDepositEnabled: boolean;
  requestedAmount?: number; // in cents
  requestedFrequency?: ContributionFrequency;
}

export default function CreatePotPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.pots' });
  const dashboardId = useSelectedDashboardId();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<PotFormData>({
    name: '',
    currency: Currency.EUR,
    description: '',
    color: PRESET_COLORS[0],
    targetAmount: undefined,
    targetDeadline: undefined,
    depositsEnabled: true,
    withdrawalsEnabled: true,
    automaticDepositEnabled: false,
    requestedAmount: undefined,
    requestedFrequency: undefined,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidHexColor = (hex: string): boolean => {
    return /^#[0-9A-F]{6}$/i.test(hex);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dashboardId) {
      toast.error(t('errors.noDashboard'));
      return;
    }

    if (!formData.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    if (formData.color && !isValidHexColor(formData.color)) {
      toast.error(t('errors.invalidColor'));
      return;
    }

    setIsSubmitting(true);
    try {
      const dto: CreatePotDto = {
        name: formData.name.trim(),
        currency: formData.currency,
        description: formData.description?.trim() || undefined,
        color: formData.color,
        targetAmount: formData.targetAmount,
        depositsEnabled: formData.depositsEnabled,
        withdrawalsEnabled: formData.withdrawalsEnabled,
        automaticDepositEnabled: formData.automaticDepositEnabled,
        requestedAmount: formData.requestedAmount,
        requestedFrequency: formData.requestedFrequency,
      };

      await potService.createPot(dashboardId, dto);
      toast.success(t('success.created'));
      navigate('/financial/pots');
    } catch (err) {
      console.error(err);
      toast.error(t('errors.createFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/financial/pots');
  };

  return (
    <>
      <PageHeader
        title={t('createModal.title')}
        subtitle={t('createModal.subtitle')}
      />

      <div className="mx-auto max-w-4xl">
        <div className="mb-4">
          <Button variant="secondary" onClick={handleCancel}>
            <TbChevronLeft className="h-5 w-5" />
            {t('back')}
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rounded-2xl bg-white p-8 shadow-sm">
            {/* Header Section */}
            <div className="mb-8 flex items-center space-x-4 border-b border-gray-200 pb-6">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-xl shadow-md"
                style={{
                  backgroundColor: isValidHexColor(formData.color) ? formData.color : PRESET_COLORS[0],
                }}>
                <TbPig className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">{t('createModal.basicInfo')}</h2>
                <p className="text-sm text-gray-500">{t('createModal.basicInfoDescription')}</p>
              </div>
            </div>

            <div className="space-y-8">
              {/* Basic Information Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900">{t('createModal.details')}</h3>
                
                {/* Name Input */}
                <Input
                  id="pot-name"
                  label={t('createModal.name')}
                  type="text"
                  placeholder={t('createModal.namePlaceholder')}
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />

                {/* Currency */}
                <Labeled label={t('createModal.currency')}>
                  <select
                    id="pot-currency"
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, currency: e.target.value as Currency }))
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50">
                    {Object.values(Currency).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Labeled>

                {/* Description Textarea */}
                <Labeled label={t('createModal.description')}>
                  <textarea
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                    rows={3}
                    placeholder={t('createModal.descriptionPlaceholder')}
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </Labeled>

                {/* Color Selector */}
                <Labeled label={t('createModal.color')}>
                  <div className="space-y-4">
                    {/* Preset Color Swatches */}
                    <div className="grid grid-cols-8 gap-2">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`group relative h-11 w-11 rounded-lg border-2 transition-all duration-200 hover:scale-110 ${
                            formData.color === color
                              ? 'border-gray-900 shadow-md'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setFormData((prev) => ({ ...prev, color }))}
                          aria-label={`Select color ${color}`}>
                          {formData.color === color && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white shadow-sm" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Color Input */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <Input
                          id="custom-color"
                          label=""
                          type="text"
                          placeholder="#3B82F6"
                          value={formData.color}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            if (value === '' || value.startsWith('#')) {
                              setFormData((prev) => ({ ...prev, color: value }));
                            }
                          }}
                          className="font-mono text-sm"
                        />
                      </div>
                      <input
                        id="color-picker"
                        type="color"
                        value={isValidHexColor(formData.color) ? formData.color : PRESET_COLORS[0]}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, color: e.target.value.toUpperCase() }))
                        }
                        className="h-11 w-11 cursor-pointer rounded-lg border-2 border-gray-300 transition-all hover:border-gray-400"
                      />
                      <div
                        className="h-11 w-11 flex-shrink-0 rounded-lg border-2 border-gray-300 shadow-sm"
                        style={{
                          backgroundColor: isValidHexColor(formData.color) ? formData.color : '#CCCCCC',
                        }}
                      />
                    </div>
                  </div>
                </Labeled>
              </div>

              {/* Goals Section */}
              <div className="space-y-5 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('createModal.goals')}</h3>

                {/* Target Amount */}
                <Labeled label={t('createModal.targetAmount')}>
                  <CurrencyInput
                    id="target-amount"
                    type="text"
                    debounced
                    value={
                      formData.targetAmount != null
                        ? (formData.targetAmount / 100).toString()
                        : ''
                    }
                    placeholder={t('createModal.targetAmountPlaceholder')}
                    handleChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        targetAmount: Number.isFinite(value)
                          ? Math.round(value * 100)
                          : undefined,
                      }))
                    }
                  />
                </Labeled>

                {/* Target Deadline */}
                <Input
                  id="target-deadline"
                  label={t('createModal.targetDeadlineLabel')}
                  type="date"
                  value={formData.targetDeadline || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, targetDeadline: e.target.value || undefined }))}
                />
              </div>

              {/* Settings Section */}
              <div className="space-y-5 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('createModal.settings')}</h3>

                {/* Deposits Enabled Toggle */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                  <div className="flex-1">
                    <label htmlFor="deposits-enabled" className="font-medium text-gray-900">
                      {t('createModal.depositsEnabled')}
                    </label>
                    <p className="text-sm text-gray-500">{t('createModal.depositsEnabledDescription')}</p>
                  </div>
                  <button
                    type="button"
                    id="deposits-enabled"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, depositsEnabled: !prev.depositsEnabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.depositsEnabled ? 'bg-primary' : 'bg-gray-300'
                    }`}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.depositsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Withdrawals Enabled Toggle */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                  <div className="flex-1">
                    <label htmlFor="withdrawals-enabled" className="font-medium text-gray-900">
                      {t('createModal.withdrawalsEnabled')}
                    </label>
                    <p className="text-sm text-gray-500">{t('createModal.withdrawalsEnabledDescription')}</p>
                  </div>
                  <button
                    type="button"
                    id="withdrawals-enabled"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, withdrawalsEnabled: !prev.withdrawalsEnabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.withdrawalsEnabled ? 'bg-primary' : 'bg-gray-300'
                    }`}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.withdrawalsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Automatic Deposit Enabled Toggle */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                  <div className="flex-1">
                    <label htmlFor="automatic-deposit-enabled" className="font-medium text-gray-900">
                      {t('createModal.automaticDepositEnabled')}
                    </label>
                    <p className="text-sm text-gray-500">{t('createModal.automaticDepositEnabledDescription')}</p>
                  </div>
                  <button
                    type="button"
                    id="automatic-deposit-enabled"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, automaticDepositEnabled: !prev.automaticDepositEnabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.automaticDepositEnabled ? 'bg-primary' : 'bg-gray-300'
                    }`}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.automaticDepositEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Requested Amount */}
                <Labeled label={t('createModal.requestedAmount')}>
                  <CurrencyInput
                    id="requested-amount"
                    type="text"
                    debounced
                    value={
                      formData.requestedAmount != null
                        ? (formData.requestedAmount / 100).toString()
                        : ''
                    }
                    placeholder={t('createModal.requestedAmountPlaceholder')}
                    handleChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        requestedAmount: Number.isFinite(value)
                          ? Math.round(value * 100)
                          : undefined,
                      }))
                    }
                  />
                </Labeled>

                {/* Requested Frequency */}
                <Labeled label={t('createModal.requestedFrequency')}>
                  <select
                    id="requested-frequency"
                    value={formData.requestedFrequency || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        requestedFrequency: e.target.value ? (e.target.value as ContributionFrequency) : undefined,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50">
                    <option value="">{t('createModal.selectFrequency')}</option>
                    <option value={ContributionFrequency.ONCE}>{t('createModal.frequency.once')}</option>
                    <option value={ContributionFrequency.MONTHLY}>{t('createModal.frequency.monthly')}</option>
                    <option value={ContributionFrequency.QUARTERLY}>{t('createModal.frequency.quarterly')}</option>
                    <option value={ContributionFrequency.YEARLY}>{t('createModal.frequency.yearly')}</option>
                  </select>
                </Labeled>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6">
                <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting} type="button">
                  {t('cancel')}
                </Button>
                <Button variant="primary-dark" type="submit" disabled={isSubmitting || !formData.name.trim()}>
                  {isSubmitting ? t('creating') : t('create')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
